/**
 * Error code to user-friendly message mapping
 * Provides consistent, user-friendly error messages across the application
 */

export interface ErrorCode {
  code: string
  message: string
  userMessage: string
  retryable: boolean
}

/**
 * Common error codes and their user-friendly messages
 */
export const ERROR_MESSAGES: Record<string, { message: string; retryable: boolean }> = {
  // Authentication errors
  'auth/unauthorized': {
    message: 'You are not authorized to perform this action',
    retryable: false,
  },
  'auth/forbidden': {
    message: 'You do not have permission to access this resource',
    retryable: false,
  },
  'auth/session-expired': {
    message: 'Your session has expired. Please log in again',
    retryable: false,
  },
  'auth/invalid-token': {
    message: 'Invalid authentication token. Please log in again',
    retryable: false,
  },

  // Network errors
  'network/offline': {
    message: 'You are currently offline. Please check your internet connection',
    retryable: true,
  },
  'network/timeout': {
    message: 'Request timed out. Please try again',
    retryable: true,
  },
  'network/failed': {
    message: 'Network request failed. Please check your connection and try again',
    retryable: true,
  },

  // Server errors
  'server/error': {
    message: 'A server error occurred. Please try again later',
    retryable: true,
  },
  'server/unavailable': {
    message: 'The server is temporarily unavailable. Please try again later',
    retryable: true,
  },

  // Profile errors
  'profile/not-found': {
    message: 'Profile not found',
    retryable: false,
  },
  'profile/update-failed': {
    message: 'Failed to update profile. Please try again',
    retryable: true,
  },

  // Friends errors
  'friends/request-failed': {
    message: 'Failed to send friend request. Please try again',
    retryable: true,
  },
  'friends/not-found': {
    message: 'Friend not found',
    retryable: false,
  },

  // Data errors
  'data/not-found': {
    message: 'The requested data was not found',
    retryable: false,
  },
  'data/load-failed': {
    message: 'Failed to load data. Please try again',
    retryable: true,
  },
}

/**
 * Get user-friendly error message from error code or error object
 */
export function getUserFriendlyError(error: Error | string | { code?: string; message?: string }): {
  message: string
  retryable: boolean
} {
  let errorCode: string | undefined
  let errorMessage: string | undefined

  if (typeof error === 'string') {
    errorMessage = error
  } else if (error instanceof Error) {
    errorMessage = error.message
    // Try to extract error code from message
    const codeMatch = error.message.match(/^(\w+\/\w+):/)
    if (codeMatch) {
      errorCode = codeMatch[1]
    }
  } else if (error && typeof error === 'object') {
    errorCode = error.code
    errorMessage = error.message
  }

  // Check if we have a mapped error code
  if (errorCode && ERROR_MESSAGES[errorCode]) {
    return ERROR_MESSAGES[errorCode]
  }

  // Check common error patterns
  if (errorMessage) {
    const lowerMessage = errorMessage.toLowerCase()

    // Network errors
    if (lowerMessage.includes('network') || lowerMessage.includes('fetch') || lowerMessage.includes('failed to fetch')) {
      return ERROR_MESSAGES['network/failed']
    }
    if (lowerMessage.includes('timeout')) {
      return ERROR_MESSAGES['network/timeout']
    }
    if (lowerMessage.includes('offline') || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      return ERROR_MESSAGES['network/offline']
    }

    // Authentication errors
    if (lowerMessage.includes('unauthorized') || lowerMessage.includes('401')) {
      return ERROR_MESSAGES['auth/unauthorized']
    }
    if (lowerMessage.includes('forbidden') || lowerMessage.includes('403')) {
      return ERROR_MESSAGES['auth/forbidden']
    }
    if (lowerMessage.includes('session') || lowerMessage.includes('token')) {
      return ERROR_MESSAGES['auth/session-expired']
    }

    // Server errors
    if (lowerMessage.includes('500') || lowerMessage.includes('server error')) {
      return ERROR_MESSAGES['server/error']
    }
    if (lowerMessage.includes('503') || lowerMessage.includes('unavailable')) {
      return ERROR_MESSAGES['server/unavailable']
    }
  }

  // Default fallback
  return {
    message: errorMessage || 'An unexpected error occurred',
    retryable: false,
  }
}

/**
 * Check if an error is retryable based on HTTP status code
 */
export function isRetryableError(status?: number, error?: Error | string): boolean {
  if (!status) {
    // Check error message for retryable patterns
    const { retryable } = getUserFriendlyError(error || '')
    return retryable
  }

  // Retryable status codes: 5xx (server errors), 408 (timeout), 429 (rate limit)
  return status >= 500 || status === 408 || status === 429
}

